/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import type {
  Deal,
  DealConstraint,
  DealConstraintSkuDetails,
  DealData,
  SkuDetails,
  ConstraintSelectionHash,
} from '../../../interface';
import { useLabels } from '@/hooks/useLabels';
import { NavBar } from '@/components/NavBar';
import styles from './Details.module.scss';
import { detailsLabelMap } from '../BundleDeal/helpers/detailsLabelMap';
import { useAddToCartErrorScroll } from '../BundleDeal/hooks/useAddToCartErrorScroll';
import { getConstraintData } from '../BundleDeal/helpers/getConstraintData';
import ConstraintDetails from './ConstraintDetails';
import BundleDetailsCardV2 from './BundleDetailsCardV2';
import AddDealToCartBtn from './AddDealToCartBtn';
import { VehicleFitmentBadge } from '../VehicleFitmentBadge';
import { useMemo } from 'react';
import NotificationBlock from '@/components/AZCustomComponent/NotificationBlock/NotificationBlockV2';
import { DealsLoader } from '../DealsLoader';

type Props = {
  parentSkuDetails: SkuDetails;
  skuIsVehicleSpecific?: boolean;
  deal: Deal;
  dealDetails?: DealData;
  constraintSelections: ConstraintSelectionHash | null;
  onCartAddClick: () => void;
  onConstraintSwapClick: (
    constraint: DealConstraint,
    constraintSkuDetails: DealConstraintSkuDetails,
  ) => void;
  isSaving: boolean;
  onBackClick?: () => void;
  onClose: () => void;
  loadError?: string | null;
  addToCartError?: string | null;
};

export const BundleDealDetails = ({
  parentSkuDetails,
  skuIsVehicleSpecific,
  deal,
  dealDetails,
  constraintSelections,
  onCartAddClick,
  onConstraintSwapClick,
  isSaving,
  onBackClick,
  onClose,
  loadError,
  addToCartError,
}: Props) => {
  const { id: dealId, dealImageUrl, name, description } = deal;
  const labels = useLabels(detailsLabelMap);

  const { scrollerRef } = useAddToCartErrorScroll(addToCartError);

  const mergedItems = useMemo(() => {
    if (!constraintSelections || !dealDetails?.constraints) {
      return [];
    }

    const { parentConstraint, secondaryConstraints } = getConstraintData(
      dealDetails.constraints,
      constraintSelections,
      parentSkuDetails,
    );

    const orderedConstraints = [
      ...(parentConstraint ? [parentConstraint] : []),
      ...secondaryConstraints,
    ];

    return orderedConstraints
      .map((constraint) => {
        const skuDetail = constraintSelections[constraint.id] as
          | DealConstraintSkuDetails
          | undefined;

        if (!skuDetail) {
          return null;
        }

        return {
          constraint,
          constraintId: constraint.id,
          minQty: constraint.minQty || 1,
          maxDiscountQty: constraint.maxDiscountQty,
          discountPercentOff: constraint.discountPercentOff,
          discountAmout: constraint.discountAmout,
          skuDetail,
          id: skuDetail.id,
          productId: skuDetail.productId,
          productImageUrl: skuDetail.productImageUrl,
          productPartNumber: skuDetail.productPartNumber,
          productBrandName: skuDetail.productBrandName,
          description: skuDetail.description,
          vehicleFit: skuDetail.vehicleFit,
          vehicleSpecfic: skuDetail.vehicleSpecfic,
          discountPrice: skuDetail.discountPrice,
          discountAmount: skuDetail.discountAmount,
          productTotalPrice: skuDetail.productTotalPrice,
          productCorePrice: skuDetail.productCorePrice,
          originalPartType: skuDetail.originalPartType,
          locationFilter: skuDetail.locationFilter,
          partGroupId: skuDetail.partGroupId,
          partTerminologyId: skuDetail.partTerminologyId,
          catalogVehicleId: skuDetail.catalogVehicleId,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [constraintSelections, dealDetails, parentSkuDetails]);

  if (loadError) {
    return (
      <div className={styles.bundleDetails}>
        <NavBar
          showBackButton={!!onBackClick}
          showCloseButton
          handleBack={onBackClick}
          handleClose={onClose}
        />
        <div className={styles.bundleDetailsScroller} ref={scrollerRef}>
          <div className={styles.errorContainer}>
            <NotificationBlock type="error" message={loadError} />
          </div>
        </div>
      </div>
    );
  }

  if (!dealDetails || !constraintSelections) {
    return (
      <DealsLoader
        showBackButton={!!onBackClick}
        showCloseButton
        handleBack={onBackClick}
        handleClose={onClose}
      />
    );
  }

  return (
    <div className={styles.bundleDetails}>
      <NavBar
        showBackButton={!!onBackClick}
        showCloseButton
        handleBack={onBackClick}
        handleClose={onClose}
      />
      <div className={styles.bundleDetailsScroller} ref={scrollerRef}>
        <div className={styles.overviewSection}>
          <h3 className={styles.title}> {labels.lblDealOverview}</h3>
          <VehicleFitmentBadge
            mode="reviewDealThatFits"
            as="span"
            className={styles.customFitmentBadge}
            skuIsVehicleSpecific={skuIsVehicleSpecific}
          />
          <BundleDetailsCardV2
            mergedItems={mergedItems}
            dealImageUrl={dealImageUrl}
            name={name}
            description={description}
          />
        </div>
        <div className={styles.content}>
          <h4 className={styles.contentTitle}> {`${labels.lblTitleDetail}:`}</h4>
          <ConstraintDetails
            loadError={loadError}
            addToCartError={addToCartError}
            mergedItems={mergedItems}
            onConstraintSwapClick={onConstraintSwapClick}
            dealId={dealId}
          />
        </div>
      </div>
      {!loadError && (
        <AddDealToCartBtn
          addToCartLabel={labels.label_deal_bundle_add_to_cart}
          onCartAddClick={onCartAddClick}
          isSaving={isSaving}
          dealId={dealId}
        />
      )}
    </div>
  );
};
